import { useAction } from '@reatom/npm-react'
import { useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import styled, { css } from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { Redacted } from '@blade/std-lib'
import { isPasswordValid } from '@root/entities/auth'
import { logoutAction } from '@root/features/auth/logout'
import { useTranslation } from '@root/shared/localization'
import {
  AuthButton,
  AuthModal,
  AuthModalHeading,
} from '@root/shared/ui/auth-modal'
import { InputPassword, LockIcon, ProfileIcon } from '@root/shared/ui-kit'

import { WrongPasswordError } from '../secure-storage-error'

import type { UnknownError } from '@root/shared'

type FormValues = {
  password: string
}

type Props = {
  state: AsyncStateN.AsyncStateN<WrongPasswordError | UnknownError, unknown>
  onSubmit: (password: Redacted.Redacted) => void
}

export const LocalPasswordForm = ({ state, onSubmit }: Props) => {
  const { t } = useTranslation()
  const logout = useAction(logoutAction)

  const { control, formState, handleSubmit, setError } = useForm<FormValues>({
    defaultValues: {
      password: '',
    },
  })

  const handleSubmitInternal = handleSubmit(async (data) => {
    onSubmit(Redacted.make(data.password))
  })

  const passwordIntent = useMemo(() => {
    if (
      AsyncStateN.isError(state) &&
      state.error instanceof WrongPasswordError
    ) {
      return 'error'
    }

    if (formState.isValid && AsyncStateN.isNotInitiated(state)) {
      return 'success'
    }

    return 'neutral'
  }, [formState, state])

  useEffect(() => {
    if (
      AsyncStateN.isError(state) &&
      state.error instanceof WrongPasswordError
    ) {
      setError('password', { message: t('auth.wrong-password') })
    }
  }, [state, setError, t])

  return (
    <AuthModal infoIcon={ProfileIcon} infoMessage={t('auth.info.wallets')}>
      <Root onSubmit={handleSubmitInternal}>
        <AuthModalHeading
          title={t('auth.unlock-your-wallets')}
          icon={LockIcon}
          intent={passwordIntent}
          mobileInfoMessage={t('auth.info.wallets')}
        />
        <Content>
          <Controller
            rules={{ required: true, validate: isPasswordValid }}
            control={control}
            name="password"
            render={({ field, fieldState }) => (
              <InputPassword
                {...field}
                autoFocus
                startContent={t('auth.password')}
                autoCorrect="off"
                spellCheck="false"
                autoComplete="password"
                size="m"
                error={fieldState.error?.message}
                css={css`
                  --input-background: var(--appearanceA-2);

                  &:focus-within {
                    --input-background-active: var(--appearanceA-5);
                  }
                `}
              />
            )}
          />
          <AuthButton disabled={!formState.isValid} busy={state.loading}>
            {t('auth.continue')}
          </AuthButton>
        </Content>
        <LogoutButton
          onClick={() => {
            logout()
          }}
        >
          {t('auth.login-with-another-account')}
        </LogoutButton>
      </Root>
    </AuthModal>
  )
}

const Root = styled.form`
  display: grid;
  grid-template-rows: auto 1fr auto;
  gap: 16px;
`

const LogoutButton = styled.div`
  color: var(--textA-9);
  font-size: 12px;
  font-weight: 500;
  text-align: center;
  transition: all 0.15s linear;

  cursor: pointer;
  &:hover {
    color: var(--textA-11);
  }
`

const Content = styled.div`
  display: grid;
  gap: 16px;
  height: 100%;
  place-content: center;
  padding-bottom: 10px;
`
